import React, { useContext, useEffect, useRef } from 'react';

import type { WithIntlProps, WrappedComponentProps } from 'react-intl';
import { injectIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import {
	ACTION,
	ACTION_SUBJECT,
	EVENT_TYPE,
	INPUT_METHOD,
} from '@atlaskit/editor-common/analytics/types/enums';
import { surfaceDragHandleElementStore } from '@atlaskit/editor-common/block-controls/surface-drag-handle-element';
import { BLOCK_MENU_TEST_ID } from '@atlaskit/editor-common/block-menu/key';
import { ErrorBoundary } from '@atlaskit/editor-common/error-boundary';
import Popup from '@atlaskit/editor-common/Popup';
import {
	DRAG_HANDLE_SELECTOR,
	NESTED_DROPDOWN_MENU,
	DRAG_HANDLE_WIDTH,
} from '@atlaskit/editor-common/styles/drag-handle';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { ArrowKeyNavigationProvider } from '@atlaskit/editor-common/ui-menu/ArrowKeyNavigationProvider';
import { ArrowKeyNavigationType } from '@atlaskit/editor-common/ui-menu/ArrowKeyNavigationProvider/types';
import { OutsideClickTargetRefContext } from '@atlaskit/editor-common/ui-react';
import withReactEditorViewOuterListeners from '@atlaskit/editor-common/ui-react/with-react-editor-view-outer-listeners';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { akEditorFloatingOverlapPanelZIndex } from '@atlaskit/editor-shared-styles/constants';
import { ToolbarMenuContainer } from '@atlaskit/editor-toolbar/toolbar-menu-container';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { Box } from '@atlaskit/primitives/compiled/box';
import { redo } from '@atlaskit/prosemirror-history/redo';
import { undo } from '@atlaskit/prosemirror-history/undo';
import { token } from '@atlaskit/tokens';

import type { BlockMenuPlugin } from '../blockMenuPluginType';
import { useBlockMenu } from './block-menu-provider';
import { BlockMenuRenderer } from './block-menu-renderer/BlockMenuRenderer';
import { BlockMenuTargetVisibilityProvider } from './block-menu-target-visibility-context';

const styles = cssMap({
	base: {
		backgroundColor: token('elevation.surface.overlay'),
		boxShadow: token('elevation.shadow.overlay'),
		borderRadius: token('radius.small'),
	},
	maxWidthStyles: {
		maxWidth: '320px',
	},
	emptyMenuSectionStyles: {
		/*
		 * This is not great - but there is no way to know using React if a specific component returns null.
		 * This style hides a menu-section if there are no menu items inside of it
		 */
		// @ts-expect-error - nested selectors are not typed in cssMap
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'[data-toolbar-component="menu-section"]:not(:has([data-toolbar-component="menu-item"]))': {
			display: 'none',
		},
		/*
		 * Hides the separator for any section that doesn't have any non-empty sections before it
		 */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'[data-toolbar-component="menu-section"]:not(:has([data-toolbar-component="menu-item"]) ~ *)': {
			borderBlockStart: 'none',
		},
	},
});

const DEFAULT_MENU_WIDTH = 230;
const DRAG_HANDLE_OFFSET_PADDING = 5;
const FALLBACK_MENU_HEIGHT = 300;

const PopupWithListeners = withReactEditorViewOuterListeners(Popup);

type BlockMenuEffectParams = {
	api: ExtractInjectionAPI<BlockMenuPlugin> | undefined;
	currentUserIntent?: string | null | undefined;
	hasFocus: boolean;
	isMenuOpen: boolean | undefined;
	menuTriggerBy: string | undefined;
	openedViaKeyboard: boolean | undefined;
	prevIsMenuOpenRef: React.MutableRefObject<boolean>;
	selectedByShortcutOrDragHandle: boolean;
};

const useConditionalBlockMenuEffect = ({
	api,
	isMenuOpen,
	menuTriggerBy,
	selectedByShortcutOrDragHandle,
	hasFocus,
	openedViaKeyboard,
	prevIsMenuOpenRef,
}: BlockMenuEffectParams) => {
	/**
	 * NOTE: do not add `currentUserIntent` to dependency array as it causes unnecessary re-renders and messes with the user intent state
	 */
	useEffect(() => {
		if (!isMenuOpen || !menuTriggerBy || !selectedByShortcutOrDragHandle || !hasFocus) {
			return;
		}

		// Fire analytics event when block menu opens (only on first transition from closed to open)
		if (!prevIsMenuOpenRef.current && isMenuOpen) {
			api?.analytics?.actions.fireAnalyticsEvent({
				action: ACTION.OPENED,
				actionSubject: ACTION_SUBJECT.BLOCK_MENU,
				eventType: EVENT_TYPE.UI,
				attributes: {
					inputMethod: openedViaKeyboard ? INPUT_METHOD.KEYBOARD : INPUT_METHOD.MOUSE,
				},
			});
		}

		// Update the previous state
		prevIsMenuOpenRef.current = isMenuOpen;

		api?.core.actions.execute(api?.userIntent?.commands.setCurrentUserIntent('blockMenuOpen'));
	}, [
		api,
		isMenuOpen,
		menuTriggerBy,
		selectedByShortcutOrDragHandle,
		hasFocus,
		openedViaKeyboard,
		prevIsMenuOpenRef,
	]);
};

export type BlockMenuProps = {
	api: ExtractInjectionAPI<BlockMenuPlugin> | undefined;
	boundariesElement?: HTMLElement;
	editorView: EditorView | undefined;
	mountTo?: HTMLElement;
	scrollableElement?: HTMLElement;
};

const isSelectionWithinCodeBlock = (state: EditorState) => {
	const { $from, $to } = state.selection;
	return $from.sameParent($to) && $from.parent.type === state.schema.nodes.codeBlock;
};
const useCloseBlockMenuOnResize = ({
	isEnabled,
	mountTo,
	boundariesElement,
	scrollableElement,
	closeMenu,
}: {
	boundariesElement?: HTMLElement;
	closeMenu: () => void;
	isEnabled: boolean;
	mountTo?: HTMLElement;
	scrollableElement?: HTMLElement;
}) => {
	useEffect(() => {
		if (!isEnabled || typeof ResizeObserver === 'undefined') {
			return;
		}

		const observedElements = Array.from(
			new Set(
				[mountTo, boundariesElement, scrollableElement].filter((element): element is HTMLElement =>
					Boolean(element),
				),
			),
		);

		if (observedElements.length === 0) {
			return;
		}

		const pendingInitialResizeElements = new Set<HTMLElement>(observedElements);
		const resizeObserver = new ResizeObserver((entries) => {
			const hasResizeAfterInitialObservation = entries.some(({ target }) => {
				if (target instanceof HTMLElement && pendingInitialResizeElements.has(target)) {
					pendingInitialResizeElements.delete(target);
					return false;
				}

				return true;
			});

			if (hasResizeAfterInitialObservation) {
				closeMenu();
			}
		});

		observedElements.forEach((element) => resizeObserver.observe(element));

		return () => {
			resizeObserver.disconnect();
		};
	}, [boundariesElement, closeMenu, isEnabled, mountTo, scrollableElement]);
};

const BlockMenuContent = ({
	api,
	setRef,
}: {
	api: ExtractInjectionAPI<BlockMenuPlugin> | undefined;
	setRef?: (el: HTMLElement | null) => void;
}) => {
	const blockMenuComponents = api?.blockMenu?.actions.getBlockMenuComponents();
	const setOutsideClickTargetRef = useContext(OutsideClickTargetRefContext);
	const ref = (el: HTMLElement | null) => {
		setOutsideClickTargetRef(el);
		setRef?.(el);
	};
	const shouldDisableArrowKeyNavigation = (event: KeyboardEvent) => {
		if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
			return false;
		}

		const target = event.target;
		if (!(target instanceof HTMLElement)) {
			return false;
		}

		return target.closest('[data-toolbar-nested-dropdown-menu]') !== null;
	};

	if (isExperimentEnabled('platform_editor_menu_radius_update')) {
		return (
			<ToolbarMenuContainer testId={BLOCK_MENU_TEST_ID} ref={ref} xcss={styles.maxWidthStyles}>
				<ArrowKeyNavigationProvider
					type={ArrowKeyNavigationType.MENU}
					// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
					handleClose={(e) => e.preventDefault()}
					disableArrowKeyNavigation={shouldDisableArrowKeyNavigation}
				>
					<BlockMenuRenderer allRegisteredComponents={blockMenuComponents || []} />
				</ArrowKeyNavigationProvider>
			</ToolbarMenuContainer>
		);
	}

	return (
		<Box
			testId={BLOCK_MENU_TEST_ID}
			role="menu"
			ref={ref}
			xcss={cx(styles.base, styles.maxWidthStyles, styles.emptyMenuSectionStyles)}
		>
			<ArrowKeyNavigationProvider
				type={ArrowKeyNavigationType.MENU}
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
				handleClose={(e) => e.preventDefault()}
				disableArrowKeyNavigation={shouldDisableArrowKeyNavigation}
			>
				<BlockMenuRenderer allRegisteredComponents={blockMenuComponents || []} />
			</ArrowKeyNavigationProvider>
		</Box>
	);
};

const BlockMenu = ({
	editorView,
	api,
	mountTo,
	boundariesElement,
	scrollableElement,
}: BlockMenuProps & WrappedComponentProps) => {
	const isPopupTargetVisibilityEnabled =
		Boolean(mountTo) && isExperimentEnabled('platform_editor_popup_target_visibility');
	const {
		menuTriggerBy,
		menuTriggerByNode,
		isSelectedViaDragHandle,
		isMenuOpen,
		currentUserIntent,
		openedViaKeyboard,
	} = useSharedPluginStateWithSelector(api, ['blockControls', 'userIntent'], (states) => ({
		menuTriggerBy: states.blockControlsState?.menuTriggerBy,
		menuTriggerByNode: isPopupTargetVisibilityEnabled
			? states.blockControlsState?.menuTriggerByNode
			: undefined,
		isSelectedViaDragHandle: states.blockControlsState?.isSelectedViaDragHandle,
		isMenuOpen: states.blockControlsState?.isMenuOpen,
		currentUserIntent: states.userIntentState?.currentUserIntent,
		openedViaKeyboard: states.blockControlsState?.blockMenuOptions?.openedViaKeyboard,
	}));
	const { onDropdownOpenChanged } = useBlockMenu();
	const isMenuOpenRef = React.useRef(isMenuOpen);
	isMenuOpenRef.current = isMenuOpen;
	const [surfaceDragHandle, setSurfaceDragHandle] = React.useState<HTMLElement | null>(null);
	React.useLayoutEffect(() => {
		if (!isExperimentEnabled('platform_editor_block_control_migration')) {
			return;
		}

		const readElement = () => setSurfaceDragHandle(surfaceDragHandleElementStore.get(editorView));
		readElement();
		return surfaceDragHandleElementStore.subscribe(editorView, () => {
			if (!isMenuOpenRef.current) {
				readElement();
			}
		});
	}, [editorView]);

	const openMenuHandleRef = React.useRef<HTMLElement | null>(null);
	const anchoredToRef = React.useRef<string | undefined>(undefined);
	if (!isMenuOpen) {
		openMenuHandleRef.current = null;
		anchoredToRef.current = undefined;
	} else if (!openMenuHandleRef.current || anchoredToRef.current !== menuTriggerBy) {
		openMenuHandleRef.current =
			surfaceDragHandleElementStore.get(editorView) ??
			openMenuHandleRef.current ??
			surfaceDragHandle;
		anchoredToRef.current = menuTriggerBy;
	}

	const targetHandleRef = isExperimentEnabled('platform_editor_block_control_migration')
		? (openMenuHandleRef.current ?? surfaceDragHandle)
		: editorView?.dom?.querySelector<HTMLElement>(DRAG_HANDLE_SELECTOR);
	const menuTargetNode = menuTriggerByNode && editorView?.nodeDOM(menuTriggerByNode.pos);
	const visibilityTarget = menuTargetNode instanceof HTMLElement ? menuTargetNode : undefined;
	const closeMenu = React.useCallback(() => {
		api?.core.actions.execute(({ tr }) => {
			api?.blockControls?.commands.toggleBlockMenu({ closeMenu: true })({ tr });
			onDropdownOpenChanged(false);
			api?.userIntent?.commands.setCurrentUserIntent(
				currentUserIntent === 'blockMenuOpen' ? 'default' : currentUserIntent || 'default',
			)({ tr });

			return tr;
		});
	}, [api, currentUserIntent, onDropdownOpenChanged]);

	useCloseBlockMenuOnResize({
		isEnabled: Boolean(isMenuOpen),
		mountTo,
		boundariesElement,
		scrollableElement,
		closeMenu,
	});
	const prevIsMenuOpenRef = useRef(false);
	const popupRef = useRef<HTMLElement | undefined>(undefined);

	const [menuHeight, setMenuHeight] = React.useState<number>(0);
	const [targetVisibility, setTargetVisibility] = React.useState<{
		isVisible: boolean | undefined;
		restoreFocusTo: HTMLElement | null;
	}>({ isVisible: undefined, restoreFocusTo: null });
	const { isVisible: targetVisible, restoreFocusTo } = targetVisibility;
	// CSS visibility can move focus to the document body. Preserve the active menu item so the
	// existing focus-loss guard does not close the menus during a temporary scroll transition.
	const handleTargetVisibilityChanged = React.useCallback((isVisible: boolean) => {
		if (isVisible) {
			setTargetVisibility((current) => ({
				isVisible: true,
				restoreFocusTo: current.isVisible === false ? current.restoreFocusTo : null,
			}));
			return;
		}

		// Capture focus before scheduling the state update so the updater stays pure.
		// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
		const activeElement = document.activeElement;
		const isFocusInMenu =
			activeElement instanceof HTMLElement &&
			(popupRef.current?.contains(activeElement) ||
				activeElement.closest(NESTED_DROPDOWN_MENU) !== null);

		setTargetVisibility({
			isVisible: false,
			restoreFocusTo: isFocusInMenu ? activeElement : null,
		});
	}, []);

	React.useLayoutEffect(() => {
		if (!isPopupTargetVisibilityEnabled || !targetVisible || !restoreFocusTo) {
			return;
		}

		const { activeElement, body, documentElement } = restoreFocusTo.ownerDocument;
		const focusWasLost =
			activeElement === null || activeElement === body || activeElement === documentElement;
		if (restoreFocusTo.isConnected && focusWasLost) {
			restoreFocusTo.focus({ preventScroll: true });
		}
		setTargetVisibility((current) => ({ ...current, restoreFocusTo: null }));
	}, [isPopupTargetVisibilityEnabled, restoreFocusTo, targetVisible]);

	const targetHandleHeightOffset = -(targetHandleRef?.clientHeight || 0);

	React.useLayoutEffect(() => {
		if (!isMenuOpen) {
			return;
		}
		onDropdownOpenChanged(true);
		setMenuHeight(popupRef.current?.clientHeight || FALLBACK_MENU_HEIGHT);
	}, [isMenuOpen, onDropdownOpenChanged]);

	// Nested dropdowns are portaled outside the block-menu popup. Sparse surface reconciliation
	// dispatches after the menu opens, so a re-render can occur while focus is inside that portal.
	const hasFocus =
		(editorView?.hasFocus() ||
			(isPopupTargetVisibilityEnabled && (targetVisible === false || restoreFocusTo !== null)) ||
			// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
			document.activeElement === targetHandleRef ||
			(popupRef.current &&
				// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
				(popupRef.current.contains(document.activeElement) ||
					// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
					popupRef.current === document.activeElement)) ||
			(isExperimentEnabled('platform_editor_block_control_migration') &&
				// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
				document.activeElement instanceof HTMLElement &&
				// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
				document.activeElement.closest(NESTED_DROPDOWN_MENU) !== null)) ??
		false;

	const selectedByShortcutOrDragHandle = !!isSelectedViaDragHandle || !!openedViaKeyboard;

	// Use conditional hook based on feature flag
	useConditionalBlockMenuEffect({
		api,
		isMenuOpen,
		menuTriggerBy,
		selectedByShortcutOrDragHandle,
		hasFocus,
		openedViaKeyboard,
		prevIsMenuOpenRef,
	});

	if (!isMenuOpen) {
		return null;
	}

	const handleKeyDown = (event: KeyboardEvent) => {
		// When the editor view has focus, the keydown will be handled by the
		// selection preservation plugin – exit early to avoid double handling
		// Also exit if selection is within a code block to avoid double handling when code block got focus when the node after it is deleted
		if (!editorView || editorView?.hasFocus() || isSelectionWithinCodeBlock(editorView.state)) {
			return;
		}

		const key = event.key.toLowerCase();
		const isMetaCtrl = event.metaKey || event.ctrlKey;
		const isDelete = ['backspace', 'delete'].includes(key);
		const isUndo = isMetaCtrl && key === 'z' && !event.shiftKey;
		const isRedo = isMetaCtrl && (key === 'y' || (key === 'z' && event.shiftKey));

		// Necessary to prevent the editor from handling the delete natively
		if (isDelete || isUndo || isRedo) {
			event.preventDefault();
			event.stopPropagation();
		}

		if (isUndo) {
			undo(editorView.state, editorView.dispatch);
		} else if (isRedo) {
			redo(editorView.state, editorView.dispatch);
		}

		api?.core?.actions.execute(
			api?.blockControls?.commands?.handleKeyDownWithPreservedSelection(event),
		);
	};

	const handleClickOutside = (e: MouseEvent) => {
		// check if the clicked element was another drag handle or nested dropdown menu, if so don't close the menu
		if (
			e.target instanceof HTMLElement &&
			(e.target.closest(DRAG_HANDLE_SELECTOR) || e.target.closest(NESTED_DROPDOWN_MENU))
		) {
			return;
		}

		closeMenu();
	};

	if (
		!menuTriggerBy ||
		!selectedByShortcutOrDragHandle ||
		!hasFocus ||
		['resizing', 'dragging'].includes(currentUserIntent || '')
	) {
		closeMenu();
		return null;
	}

	const setRef = (el: HTMLElement | null) => {
		if (el) {
			popupRef.current = el;
		}
	};

	if (!(targetHandleRef instanceof HTMLElement)) {
		return null;
	}
	const blockMenuContent = <BlockMenuContent api={api} setRef={setRef} />;

	return (
		<ErrorBoundary
			component={ACTION_SUBJECT.BLOCK_MENU}
			dispatchAnalyticsEvent={api?.analytics?.actions.fireAnalyticsEvent}
			fallbackComponent={null}
		>
			<PopupWithListeners
				alignX={'right'}
				alignY={'start'}
				handleClickOutside={handleClickOutside}
				handleEscapeKeydown={closeMenu}
				handleKeyDown={handleKeyDown}
				mountTo={mountTo}
				boundariesElement={boundariesElement}
				scrollableElement={scrollableElement}
				target={targetHandleRef}
				visibilityTarget={visibilityTarget}
				zIndex={akEditorFloatingOverlapPanelZIndex}
				fitWidth={DEFAULT_MENU_WIDTH}
				fitHeight={menuHeight}
				preventOverflow={true}
				stick={true}
				hideWhenTargetOutOfView={isPopupTargetVisibilityEnabled}
				onTargetVisibilityChanged={
					isPopupTargetVisibilityEnabled ? handleTargetVisibilityChanged : undefined
				}
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
				offset={[DRAG_HANDLE_WIDTH + DRAG_HANDLE_OFFSET_PADDING, targetHandleHeightOffset]}
				focusTrap={
					openedViaKeyboard
						? // Only enable focus trap when opened via keyboard to make sure the focus is on the first focusable menu item
							{ initialFocus: undefined }
						: undefined
				}
			>
				{isPopupTargetVisibilityEnabled ? (
					<BlockMenuTargetVisibilityProvider value={targetVisible}>
						{blockMenuContent}
					</BlockMenuTargetVisibilityProvider>
				) : (
					blockMenuContent
				)}
			</PopupWithListeners>
		</ErrorBoundary>
	);
};

// eslint-disable-next-line @typescript-eslint/no-restricted-types
const _default_1: React.FC<WithIntlProps<BlockMenuProps & WrappedComponentProps>> & {
	WrappedComponent: React.ComponentType<BlockMenuProps & WrappedComponentProps>;
} = injectIntl(BlockMenu);
export default _default_1;
